import React from "react";
import { Box, Typography, Skeleton, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LANE_KEYS, LANE_CONFIG } from "./boardConfig";

function SummaryCard({ laneKey, count, loading, isDark }) {
  const config = LANE_CONFIG[laneKey];
  const Icon = config.Icon;

  return (
    <Box
      sx={{
        borderRadius: "18px",
        border: `1px solid ${isDark ? config.borderDark : config.borderLight}`,
        background: isDark ? config.bgDark : config.bgLight,
        backdropFilter: "blur(8px)",
        p: 2.25,
        height: "100%",
        boxShadow: isDark
          ? "0 2px 8px rgba(0,0,0,0.28), 0 1px 2px rgba(0,0,0,0.20)"
          : "0 2px 8px rgba(15,23,42,0.07), 0 1px 2px rgba(15,23,42,0.04)",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: isDark
            ? "0 8px 24px rgba(0,0,0,0.38)"
            : "0 8px 24px rgba(15,23,42,0.10)",
        },
        ...(laneKey === "overdue" && {
          border: `1px solid ${isDark ? "rgba(248,113,113,0.22)" : "rgba(239,68,68,0.20)"}`,
          boxShadow: isDark
            ? "0 0 0 1px rgba(248,113,113,0.08)"
            : "0 0 0 1px rgba(239,68,68,0.06)",
        }),
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.78rem", fontWeight: 500 }}
          >
            {config.label}
          </Typography>

          {loading ? (
            <Skeleton width={48} height={44} sx={{ mt: 0.5 }} />
          ) : (
            <Typography
              variant="h4"
              sx={{ mt: 0.5, lineHeight: 1, fontWeight: 800, fontSize: { xs: "1.7rem", md: "2rem" }, letterSpacing: "-0.03em" }}
            >
              {count ?? 0}
            </Typography>
          )}

          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ mt: 0.6, display: "block", fontSize: "0.72rem" }}
          >
            {config.sub}
          </Typography>
        </Box>

        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isDark ? config.chipBgDark : config.chipBgLight,
            flexShrink: 0,
          }}
        >
          <Icon
            sx={{
              fontSize: 18,
              color: config.dotColor,
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
}

export default function BoardSummaryStrip({ summary, loading }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
        gap: 2,
      }}
    >
      {LANE_KEYS.map((key) => (
        <SummaryCard
          key={key}
          laneKey={key}
          count={summary?.[key]}
          loading={loading}
          isDark={isDark}
        />
      ))}
    </Box>
  );
}
